import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { ErrorBoundary } from './components/error-boundary'
import { installDomMutationGuard } from './lib/dom-guard'
import { reportClientError } from './lib/telemetry'
import './styles/index.css'

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!CLERK_KEY) {
  throw new Error(
    'VITE_CLERK_PUBLISHABLE_KEY is not set. Copy apps/web/.env.local.example to .env.local and fill it in.',
  )
}

const router = createRouter({ routeTree, defaultPreload: 'intent' })
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Before anything renders: Chrome's page translation mangles the nodes React
// holds onto, which used to take the whole screen down with a NotFoundError.
installDomMutationGuard()

// Errors that escape React entirely — event handlers, async callbacks, and
// rejected promises nobody awaited. Without these the app has no record of them
// at all: they never reach the server, so no server log can hold them.
window.addEventListener('error', (event) => {
  reportClientError({
    kind: 'window',
    message: event.message || String(event.error ?? 'unknown error'),
    stack: event.error instanceof Error ? event.error.stack : null,
  })
})

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  reportClientError({
    kind: 'promise',
    message: reason instanceof Error ? reason.message : String(reason),
    stack: reason instanceof Error ? reason.stack : null,
  })
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_KEY}
      // Land users in the app after auth instead of Clerk's Account Portal.
      // `Fallback` = used only when the URL carries no explicit redirect_url
      // (e.g. on invitation links), so it never fights a deliberate redirect.
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <QueryClientProvider client={queryClient}>
        {/* Inside the providers, so a crashing screen keeps auth and query
            context alive and the fallback can still reload cleanly. */}
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
)
